// AccountSettings.js
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  HStack,
  VStack,
  Avatar,
  AvatarBadge,
  Icon,
  Button,
  Divider,
  useToast,
  useColorModeValue,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Flex,
} from "@chakra-ui/react";
import { CheckIcon, CloseIcon, EditIcon } from "@chakra-ui/icons";
import {
  updateAccountProfile,
  fetchAccountProfile,
  changeAccountPassword,
  checkPasswordChangeEligibility,
  deactivateAccount,
  checkAccountDeactivationStatus,
  restoreAccount,
} from "../../api/accountSettings";

const AccountSettings = () => {
  const [user, setUser] = useState({});
  const [draft, setDraft] = useState({});
  const [editingField, setEditingField] = useState(null); // NEW: track which field is being edited
  const [saving, setSaving] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loadingPasswordChange, setLoadingPasswordChange] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordEligibility, setPasswordEligibility] = useState({
    can_change_password: true,
    is_google_user: false,
    message: "",
  });
  const [deactivationStatus, setDeactivationStatus] = useState(null);

  const toast = useToast();
  const cardBg = useColorModeValue("white", "gray.700");
  const cancelRef = useRef();

  // Email validation function
  const isValidEmail = (email) => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  };

  // Check if current field is valid for saving
  const isCurrentFieldValid = () => {
    if (!editingField) return false;

    switch (editingField) {
      case "firstName":
      case "lastName":
        // These fields are optional, so any value is valid
        return true;
      case "gender":
        return !!draft.gender;
      case "dob":
        return !!draft.dob;
      case "username":
        return draft.username && draft.username.length >= 3;
      case "email":
        return draft.email && isValidEmail(draft.email);
      default:
        return false;
    }
  };

  // Check if user can make changes (rate limit check)
  const canMakeChanges = () => {
    return user.changeCount === undefined || user.changeCount < 6;
  };

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const res = await fetchAccountProfile();
        // Map backend fields to frontend state
        const data = res.data || res.result || res;
        const userData = {
          firstName: data.first_name || "",
          lastName: data.last_name || "",
          username: data.username || "",
          email: data.email || "",
          gender: data.gender || "",
          dob: data.date_of_birth || "",
          emailVerified: data.email_verified || false,
          loggedInWithGoogle: data.logged_in_with_google || false,
          changeCount: data.change_count || 0,
          lastChangeTimestamp: data.last_change_timestamp || "",
          deactivatedAt: data.deactivated_at || "",
          deactivationReason: data.deactivation_reason || "",
        };
        setUser(userData);
        setDraft(userData);
      } catch (error) {
        toast({
          title:
            error?.response?.data?.message ||
            error.message ||
            "Failed to load profile",
          status: "error",
        });
      }
    };
    const loadPasswordEligibility = async () => {
      try {
        const res = await checkPasswordChangeEligibility();
        const data = res.data || res.result || res;
        setPasswordEligibility(data);
      } catch (error) {
        setPasswordEligibility({
          can_change_password: false,
          is_google_user: false,
          message: "Unable to change password.",
        });
      }
    };
    const loadDeactivationStatus = async () => {
      try {
        const res = await checkAccountDeactivationStatus();
        const data = res.data || res.result || res;
        setDeactivationStatus(data);
      } catch (error) {
        setDeactivationStatus(null);
      }
    };
    loadProfile();
    loadPasswordEligibility();
    loadDeactivationStatus();
    // eslint-disable-next-line
  }, []);

  const onField = (key) => (e) => setDraft({ ...draft, [key]: e.target.value });

  const handleSave = async () => {
    setSaving(true);

    // Create payload with only the field being edited
    let payload = {};

    switch (editingField) {
      case "firstName":
        payload = { first_name: draft.firstName };
        break;
      case "lastName":
        payload = { last_name: draft.lastName };
        break;
      case "username":
        if (!draft.username || draft.username.length < 3) {
          toast({
            title: "Invalid username",
            description: "Username must be at least 3 characters long",
            status: "error",
          });
          setSaving(false);
          return;
        }
        payload = { username: draft.username };
        break;
      case "email":
        if (!draft.email || !isValidEmail(draft.email)) {
          toast({
            title: "Invalid email",
            description: "Please enter a valid email address",
            status: "error",
          });
          setSaving(false);
          return;
        }
        payload = { email: draft.email };
        break;
      case "gender":
        if (!draft.gender) {
          toast({
            title: "Required field missing",
            description: "Gender is required",
            status: "error",
          });
          setSaving(false);
          return;
        }
        payload = { gender: draft.gender };
        break;
      case "dob":
        if (!draft.dob) {
          toast({
            title: "Required field missing",
            description: "Date of birth is required",
            status: "error",
          });
          setSaving(false);
          return;
        }
        payload = { date_of_birth: draft.dob };
        break;
      default:
        toast({
          title: "Invalid field",
          description: "Unknown field being edited",
          status: "error",
        });
        setSaving(false);
        return;
    }

    try {
      const res = await updateAccountProfile(payload);

      // Check if the response indicates success
      if (res && (res.success || res.status === "success")) {
        // Update only the specific field in user state
        setUser((prevUser) => ({
          ...prevUser,
          [editingField === "dob" ? "dob" : editingField]:
            draft[editingField === "dob" ? "dob" : editingField],
        }));

        setEditingField(null);
        const fieldName =
          editingField === "firstName"
            ? "First Name"
            : editingField === "lastName"
            ? "Last Name"
            : editingField === "username"
            ? "Username"
            : editingField === "email"
            ? "Email"
            : editingField === "gender"
            ? "Gender"
            : editingField === "dob"
            ? "Date of Birth"
            : editingField;

        toast({
          title: res?.message || `${fieldName} updated successfully`,
          status: "success",
        });

        // Reload profile data to get updated rate limiting info
        const profileRes = await fetchAccountProfile();
        if (profileRes.data || profileRes.result) {
          const data = profileRes.data || profileRes.result || profileRes;
          setUser((prevUser) => ({
            ...prevUser,
            changeCount: data.change_count || 0,
            lastChangeTimestamp: data.last_change_timestamp || "",
          }));
        }
      } else {
        throw new Error(res?.message || "Update failed");
      }
    } catch (error) {
      // Handle specific error cases
      let errorMessage = "Failed to update profile";

      if (error?.response?.status === 429) {
        errorMessage =
          error?.response?.data?.message ||
          "Rate limit exceeded. You can make changes again in 1 hour.";
      } else if (error?.response?.status === 400) {
        errorMessage =
          error?.response?.data?.message || "Invalid data provided";
      } else if (error?.response?.status === 403) {
        errorMessage = "Cannot update profile for deactivated account";
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        title: errorMessage,
        status: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const res = await deactivateAccount();
      toast({
        title: res?.message || res?.data?.message || "",
        description: res?.description || res?.data?.description || "",
        status: "success",
      });
      setShowConfirmDelete(false);
      // Reload the page to reflect deactivation status
      if (typeof window !== "undefined") window.location.reload();
    } catch (error) {
      toast({
        title:
          error?.response?.data?.message ||
          error.message ||
          "Failed to deactivate account",
        status: "error",
      });
    }
  };

  const handleChangePassword = async () => {
    setPasswordError("");
    const password = newPassword.trim();
    const confirm = confirmPassword.trim();
    const strongRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
    if (password !== confirm) {
      setPasswordError("Passwords do not match.");
      return;
    }
    if (!strongRegex.test(password)) {
      setPasswordError(
        "Password must be at least 8 characters and include uppercase, lowercase, number, and special character."
      );
      return;
    }
    try {
      setLoadingPasswordChange(true);
      const res = await changeAccountPassword(password, confirm);
      toast({
        title: res?.message || res?.data?.message || "",
        status: "success",
      });
      setShowPasswordForm(false);
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setPasswordError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to change password. Try again."
      );
    } finally {
      setLoadingPasswordChange(false);
    }
  };

  const handleRestore = async () => {
    try {
      const res = await restoreAccount();
      toast({
        title: res?.message || res?.data?.message || "",
        description: res?.description || res?.data?.description || "",
        status: "success",
      });
      // Refresh deactivation status and profile
      if (typeof window !== "undefined") window.location.reload();
    } catch (error) {
      toast({
        title:
          error?.response?.data?.message ||
          error.message ||
          "Failed to restore account",
        status: "error",
      });
    }
  };

  return (
    <Box flex="1" h="92vh" overflowY="auto" p={8} pt={6}>
      <Box bg={cardBg}>
        {deactivationStatus && deactivationStatus.deactivated && (
          <Box
            mb={4}
            p={4}
            bg="yellow.100"
            borderRadius="md"
            border="1px solid #ECC94B"
          >
            <Text color="yellow.800" fontWeight="bold">
              {deactivationStatus.message}
            </Text>
            {deactivationStatus.deactivated_at && (
              <Text color="yellow.800" fontSize="sm" mt={1}>
                Deactivated on:{" "}
                {new Date(deactivationStatus.deactivated_at).toLocaleString()}
              </Text>
            )}
            {deactivationStatus.can_restore && (
              <Button mt={2} colorScheme="yellow" onClick={handleRestore}>
                Restore Account
              </Button>
            )}
          </Box>
        )}
        <Heading size="md" mb={4}>
          My Profile
        </Heading>
        <Divider mb={8} />
        <HStack mb={10} spacing={6} align="center">
          <Avatar size="lg" name={user.firstName}>
            {user.emailVerified && (
              <AvatarBadge boxSize="1.25em" bg="green.400">
                <CheckIcon fontSize="0.6rem" />
              </AvatarBadge>
            )}
          </Avatar>
          {/* Removed Edit Information button here */}
        </HStack>

        <HStack spacing={6} mb={6} align="center">
          <FormControl>
            <Flex align="center" gap={0} mb={1}>
              <FormLabel mb={0} mr={1}>
                First name
              </FormLabel>

              {editingField === "firstName" ? (
                <>
                  <Button
                    size="sm"
                    mr={2}
                    borderRadius="lg"
                    onClick={handleSave}
                    isLoading={saving}
                    p={1}
                    isDisabled={!isCurrentFieldValid()}
                  >
                    <CheckIcon color="green.500" />
                  </Button>
                  <Button
                    size="sm"
                    borderRadius="lg"
                    onClick={() => {
                      setDraft(user);
                      setEditingField(null);
                    }}
                    p={1}
                  >
                    <CloseIcon color="red.500" />
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingField("firstName")}
                  p={1}
                  isDisabled={!canMakeChanges()}
                  title={
                    !canMakeChanges()
                      ? "Rate limit reached. Try again in 1 hour."
                      : "Edit first name"
                  }
                >
                  <EditIcon />
                </Button>
              )}
            </Flex>
            <Input
              value={draft.firstName}
              onChange={onField("firstName")}
              isDisabled={editingField !== "firstName"}
            />
          </FormControl>
          <FormControl>
            <Flex align="center" gap={0} mb={1}>
              <FormLabel mb={0} mr={1}>
                Last name
              </FormLabel>

              {editingField === "lastName" ? (
                <>
                  <Button
                    size="sm"
                    mr={2}
                    borderRadius="lg"
                    onClick={handleSave}
                    isLoading={saving}
                    p={1}
                    isDisabled={!isCurrentFieldValid()}
                  >
                    <CheckIcon color="green.500" />
                  </Button>
                  <Button
                    size="sm"
                    borderRadius="lg"
                    onClick={() => {
                      setDraft(user);
                      setEditingField(null);
                    }}
                    p={1}
                  >
                    <CloseIcon color="red.500" />
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingField("lastName")}
                  p={1}
                  isDisabled={!canMakeChanges()}
                  title={
                    !canMakeChanges()
                      ? "Rate limit reached. Try again in 1 hour."
                      : "Edit last name"
                  }
                >
                  <EditIcon />
                </Button>
              )}
            </Flex>
            <Input
              value={draft.lastName}
              onChange={onField("lastName")}
              isDisabled={editingField !== "lastName"}
            />
          </FormControl>
        </HStack>

        <FormControl mb={6} w="70%">
          <Flex align="center" gap={0} mb={1}>
            <FormLabel mb={0} mr={1}>
              Username
            </FormLabel>
            {editingField === "username" ? (
              <>
                <Button
                  size="sm"
                  mr={2}
                  borderRadius="lg"
                  onClick={handleSave}
                  isLoading={saving}
                  p={1}
                  isDisabled={!isCurrentFieldValid()}
                >
                  <CheckIcon color="green.500" />
                </Button>
                <Button
                  size="sm"
                  borderRadius="lg"
                  onClick={() => {
                    setDraft(user);
                    setEditingField(null);
                  }}
                  p={1}
                >
                  <CloseIcon color="red.500" />
                </Button>
              </>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingField("username")}
                p={1}
                isDisabled={!canMakeChanges()}
                title={
                  !canMakeChanges()
                    ? "Rate limit reached. Try again in 1 hour."
                    : "Edit username"
                }
              >
                <EditIcon />
              </Button>
            )}
          </Flex>
          <Input
            value={draft.username}
            onChange={onField("username")}
            isDisabled={editingField !== "username"}
            isInvalid={
              editingField === "username" &&
              draft.username &&
              draft.username.length < 3
            }
          />
          {editingField === "username" &&
            draft.username &&
            draft.username.length < 3 && (
              <Text color="red.500" fontSize="xs" mt={1}>
                Username must be at least 3 characters long
              </Text>
            )}
        </FormControl>

        <HStack align="flex-end" mb={6} w="100%">
          <FormControl w="70%">
            <Flex align="center" gap={0} mb={1}>
              <FormLabel mb={0} mr={1}>
                Email
              </FormLabel>
              {editingField === "email" ? (
                <>
                  <Button
                    size="sm"
                    mr={2}
                    borderRadius="lg"
                    onClick={handleSave}
                    isLoading={saving}
                    p={1}
                    isDisabled={!isCurrentFieldValid()}
                  >
                    <CheckIcon color="green.500" />
                  </Button>
                  <Button
                    size="sm"
                    borderRadius="lg"
                    onClick={() => {
                      setDraft(user);
                      setEditingField(null);
                    }}
                    p={1}
                  >
                    <CloseIcon color="red.500" />
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingField("email")}
                  p={1}
                  isDisabled={!canMakeChanges()}
                  title={
                    !canMakeChanges()
                      ? "Rate limit reached. Try again in 1 hour."
                      : "Edit email"
                  }
                >
                  <EditIcon />
                </Button>
              )}
            </Flex>
            <InputGroup>
              <Input
                value={draft.email}
                onChange={onField("email")}
                isDisabled={editingField !== "email"}
                pr="2.5rem"
                isInvalid={
                  editingField === "email" &&
                  draft.email &&
                  !isValidEmail(draft.email)
                }
              />
              <InputRightElement pointerEvents="none">
                {user.emailVerified && (
                  <Icon as={CheckIcon} color="green.400" />
                )}
              </InputRightElement>
            </InputGroup>
            {editingField === "email" &&
              draft.email &&
              !isValidEmail(draft.email) && (
                <Text color="red.500" fontSize="xs" mt={1}>
                  Please enter a valid email address
                </Text>
              )}
          </FormControl>
        </HStack>

        <HStack spacing={6} mb={10}>
          <FormControl>
            <Flex align="center" gap={0} mb={1}>
              <FormLabel mb={0} mr={1}>
                Gender
              </FormLabel>
              {editingField === "gender" ? (
                <>
                  <Button
                    size="sm"
                    mr={2}
                    borderRadius="lg"
                    onClick={handleSave}
                    isLoading={saving}
                    p={1}
                    // isDisabled={!isCurrentFieldValid()}
                  >
                    <CheckIcon color="green.500" />
                  </Button>
                  <Button
                    size="sm"
                    borderRadius="lg"
                    onClick={() => {
                      setDraft(user);
                      setEditingField(null);
                    }}
                    p={1}
                  >
                    <CloseIcon color="red.500" />
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingField("gender")}
                  p={1}
                  isDisabled={!canMakeChanges()}
                  title={
                    !canMakeChanges()
                      ? "Rate limit reached. Try again in 1 hour."
                      : "Edit gender"
                  }
                >
                  <EditIcon />
                </Button>
              )}
            </Flex>
            <Select
              placeholder="Select gender"
              value={draft.gender}
              onChange={onField("gender")}
              isDisabled={editingField !== "gender"}
            >
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </Select>
          </FormControl>
          <FormControl>
            <Flex align="center" gap={0} mb={1}>
              <FormLabel mb={0} mr={1}>
                Date of birth
              </FormLabel>
              {editingField === "dob" ? (
                <>
                  <Button
                    size="sm"
                    mr={2}
                    borderRadius="lg"
                    onClick={handleSave}
                    isLoading={saving}
                    p={1}
                    isDisabled={!isCurrentFieldValid()}
                  >
                    <CheckIcon color="green.500" />
                  </Button>
                  <Button
                    size="sm"
                    borderRadius="lg"
                    onClick={() => {
                      setDraft(user);
                      setEditingField(null);
                    }}
                    p={1}
                  >
                    <CloseIcon color="red.500" />
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingField("dob")}
                  p={1}
                  isDisabled={!canMakeChanges()}
                  title={
                    !canMakeChanges()
                      ? "Rate limit reached. Try again in 1 hour."
                      : "Edit date of birth"
                  }
                >
                  <EditIcon />
                </Button>
              )}
            </Flex>
            <Input
              type="date"
              value={draft.dob}
              onChange={onField("dob")}
              isDisabled={editingField !== "dob"}
              max={new Date().toISOString().split("T")[0]}
            />
          </FormControl>
        </HStack>

        {/* {editingField && (
          <HStack>
            <Button
              bg="black"
              color="white"
              _hover="none"
              onClick={handleSave}
              isLoading={saving}
            >
              Save
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setDraft(user);
                setEditingField(null);
              }}
            >
              Cancel
            </Button>
            <Spacer />
            <Text fontSize="sm" color="gray.500">
              {editsLeft} changes left this hour
            </Text>
          </HStack>
        )} */}

        <Heading size="md" mb={4} mt={12}>
          Security
        </Heading>
        <Divider mb={8} />

        <Box mb={10}>
          <Text fontWeight="medium" mb={2}>
            Would you like to change your password?
          </Text>

          {!passwordEligibility.can_change_password ? (
            <Text fontSize="sm" color="gray.500">
              {passwordEligibility.message}
            </Text>
          ) : !showPasswordForm ? (
            <HStack align="center" mb={3}>
              <Input type="password" value="••••••••" isDisabled w="80%" />
              <Button
                variant="outline"
                onClick={() => setShowPasswordForm(true)}
                w="20%"
              >
                Change password
              </Button>
            </HStack>
          ) : (
            <form
              style={{ width: "100%" }}
              onSubmit={(e) => {
                e.preventDefault();
                handleChangePassword();
              }}
            >
              <VStack align="flex-start" spacing={4} mt={4} w="100%">
                <FormControl>
                  <FormLabel>New Password</FormLabel>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Confirm New Password</FormLabel>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </FormControl>
                <HStack w="100%" justify="flex-end">
                  <Button
                    type="submit"
                    bg="black"
                    color="white"
                    _hover="none"
                    isDisabled={loadingPasswordChange}
                    isLoading={loadingPasswordChange}
                  >
                    Save Password
                  </Button>
                  <Button
                    variant="ghost"
                    type="button"
                    onClick={() => {
                      setShowPasswordForm(false);
                      setNewPassword("");
                      setConfirmPassword("");
                      setPasswordError("");
                    }}
                  >
                    Cancel
                  </Button>
                </HStack>
                {passwordError && (
                  <Text color="red.500" fontSize="sm">
                    {passwordError}
                  </Text>
                )}
              </VStack>
            </form>
          )}
        </Box>

        <HStack
          w="100%"
          py={6}
          borderTop="1px solid"
          borderColor={useColorModeValue("gray.200", "gray.600")}
          justify="space-between"
          align="flex-start"
        >
          <Box>
            <Text fontWeight="semibold" color="red.600" mb={1}>
              Deactivate account
            </Text>
            <Text fontSize="sm" color="gray.500">
              Permanently delete the account and remove access from all
              workspaces.
            </Text>
          </Box>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowConfirmDelete(true)}
          >
            Deactivate account
          </Button>
        </HStack>
      </Box>

      <AlertDialog
        isOpen={showConfirmDelete}
        leastDestructiveRef={cancelRef}
        onClose={() => setShowConfirmDelete(false)}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader>Confirm deactivation</AlertDialogHeader>
          <AlertDialogBody>
            Deleting your account will deactivate it and all your data will be
            removed according to our privacy policy. You can recover your
            account within 14 days by signing in again. After 14 days, recovery
            will not be possible.
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={() => setShowConfirmDelete(false)}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={handleDelete} ml={3}>
              Confirm
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Box>
  );
};

export default AccountSettings;
